<template>
  <div
    class="bonus-uhid"
    :class="{
      'bonus-uhid--subpage': isSubpage,
      'bonus-uhid--campaign-detail': campaignPageDetail,
    }"
  >
    <div class="bonus-uhid__header">
      <div class="bonus-uhid__heading">
        <span class="bonus-uhid__badge">
          <img :src="bonusIconSrc" alt="" aria-hidden="true" />
        </span>
        <h3 class="bonus-uhid__title">{{ $t("portalHome.bonusUhrd.title") }}</h3>
      </div>
    </div>

    <div class="bonus-uhid__progress">
      <div class="bonus-uhid__track">
        <div class="bonus-uhid__bar-wrap">
          <div class="bonus-uhid__labels-band">
            <span
              class="bonus-uhid__progress-label bonus-uhid__progress-label--points"
              :class="{ 'bonus-uhid__progress-label--points-end': pointsLabelEndAlign }"
              :style="{ left: goalMarkerLeftPct + '%' }"
            >{{ $t(data.pointsLabelKey) }}</span>
            <span
              v-if="showGoalAchievedUi && data.beyondGoalPointsLabelKey"
              class="bonus-uhid__progress-label bonus-uhid__progress-label--points bonus-uhid__progress-label--beyond-end"
            ><span class="bonus-uhid__beyond-rate-pts">{{
              $t(data.beyondGoalPointsLabelKey)
            }}</span> <span class="bonus-uhid__beyond-rate-unit">{{
              $t(beyondGoalPointsPerUnitKey)
            }}</span></span>
          </div>
          <div class="bonus-uhid__progress-bar">
            <div
              v-for="(seg, i) in barSegments"
              :key="`seg-${i}`"
              class="bonus-uhid__track-segment"
              :class="{
                'bonus-uhid__track-segment--tail':
                  !seg.gapRight && (isSubpage || campaignPageDetail),
              }"
              :style="segmentStyle(seg)"
            ></div>
            <template v-if="showGoalAchievedUi">
              <div
                class="bonus-uhid__progress-fill"
                :style="goalReachedFillStyle"
              ></div>
              <div
                class="bonus-uhid__progress-gap"
                :style="{
                  left: goalMarkerLeftPct + '%',
                  width: GOAL_ACHIEVED_GAP_PX + 'px',
                }"
                aria-hidden="true"
              ></div>
              <div
                class="bonus-uhid__progress-fill"
                :style="beyondGoalFillStyle"
              ></div>
            </template>
            <div
              v-else
              class="bonus-uhid__progress-fill"
              :style="{ width: progressPct + '%' }"
            ></div>
            <div
              v-for="(level, i) in uhidVisibleTicks"
              :key="i"
              class="bonus-uhid__tick"
              :style="{ left: getTickPosition(level.threshold) + '%' }"
              :class="{ 'bonus-uhid__tick--active': level.active }"
            ></div>
          </div>
          <span
            class="bonus-uhid__units-under-bar"
            :style="unitsUnderBarStyle"
          >{{ $t("portalHome.units.piecesCount", { count: data.currentUnits }) }}</span>
        </div>

        <div class="bonus-uhid__goal-under">
          <span
            v-if="showGoalAchievedUi"
            class="bonus-uhid__goal-pill bonus-uhid__goal-pill--achieved"
            :style="goalAchievedPillStyle"
          >{{ $t(data.goalAchievedLabelKey, { count: data.goal }) }}</span>
          <span
            v-else
            class="bonus-uhid__goal-pill"
            :class="goalPillAlignClass(goalMarkerLeftPct)"
            :style="{ left: goalMarkerLeftPct + '%' }"
          >{{ $t("portalHome.bonusUhrd.goalLabel", { count: data.goal }) }}</span>
        </div>
      </div>
    </div>

    <footer class="bonus-uhid__footer" :class="{ 'bonus-uhid__footer--subpage': isSubpage }">
      <template v-if="isSubpage">
        <p v-if="campaignPageDetail" class="bonus-uhid__period bonus-uhid__period--campaign">
          <span class="bonus-uhid__period-lead">{{ $t(`${periodBlockPrefix}.periodLead`) }}</span>
          <strong class="bonus-uhid__period-dates">{{ $t(`${periodBlockPrefix}.periodDates`) }}</strong>
        </p>
        <p v-else class="bonus-uhid__period">{{ $t(periodLabelKey) }}</p>
        <button
          type="button"
          class="bonus-uhid__regs"
          :class="{ 'bonus-uhid__regs--campaign': campaignPageDetail }"
        >{{ $t("portalHome.bonusUhrd.regsButton") }}</button>
      </template>
      <RouterLink
        v-else
        class="bonus-uhid__cta bonus-uhid__cta--route"
        :to="{ name: 'bonusUhrd' }"
      >
        {{ $t("portalHome.bonusUhrd.detailsButton") }}
      </RouterLink>
    </footer>
  </div>
</template>

<script setup>
import { computed, defineProps } from "vue";
import { RouterLink } from "vue-router";
import { portalHomeIcons } from "./portalHomeAssets";

const props = defineProps({
  data: { type: Object, required: true },
  variant: {
    type: String,
    default: "home",
    validator: (v) => v === "home" || v === "subpage",
  },
  periodLabelKey: { type: String, default: "portalHome.bonusUhrdPage.bonusPeriodLine" },
  campaignPageDetail: { type: Boolean, default: false },
  periodBlockPrefix: { type: String, default: "portalHome.bonusUhrdPage" },
});

const isSubpage = computed(() => props.variant === "subpage");

const isGoalAchieved = computed(() => props.data.goalAchieved === true);

const showGoalAchievedUi = computed(() => isGoalAchieved.value);

const bonusIconSrc = portalHomeIcons.bonusUhrd;
const beyondGoalPointsPerUnitKey = "portalHome.bonusUhrd.beyondGoalPointsPerUnit";

const safeLevels = computed(() =>
  Array.isArray(props.data.levels) ? props.data.levels : []
);

const maxThreshold = computed(() => {
  const levels = safeLevels.value;
  const last = levels[levels.length - 1];
  return last?.threshold ?? 160;
});

const TAIL_RATIO = 0.08;
const GOAL_ACHIEVED_ON_BAR_PCT = 50;

const positionScale = computed(() => {
  if (showGoalAchievedUi.value) {
    const goal = Number(props.data.goal);
    const onBarPct =
      Number(props.data.goalBarPositionPct) || GOAL_ACHIEVED_ON_BAR_PCT;
    if (Number.isFinite(goal) && goal > 0 && onBarPct > 0) {
      return goal / (onBarPct / 100);
    }
  }
  return Math.max(maxThreshold.value, 1) / (1 - TAIL_RATIO);
});

const progressPct = computed(() =>
  Math.min(
    ((Number(props.data.currentUnits) || 0) / positionScale.value) * 100,
    100
  )
);

const unitsUnderBarStyle = computed(() => {
  const p = Math.min(Math.max(progressPct.value, 0), 100);
  if (p <= 0) {
    return { left: "0%", transform: "translateX(0)" };
  }
  if (showGoalAchievedUi.value && p < 14) {
    return { left: `${p}%`, transform: "translateX(0)", marginLeft: "2px" };
  }
  if (showGoalAchievedUi.value) {
    return { left: `${p}%`, transform: "translateX(calc(-100% - 2px))" };
  }
  return { left: `${p}%`, transform: "translateX(-50%)" };
});

const uhidVisibleTicks = computed(() =>
  safeLevels.value.filter((l) => {
    const t = Number(l.threshold);
    if (t <= 0) return false;
    if (showGoalAchievedUi.value) {
      if (t === maxThreshold.value) return false;
      if (t === Number(props.data.goal)) return false;
    }
    return true;
  })
);

const getTickPosition = (threshold) =>
  Math.min((threshold / positionScale.value) * 100, 100);

const goalMarkerLeftPct = computed(() => {
  const g = Number(props.data.goal);
  if (!Number.isFinite(g) || positionScale.value <= 0) return 100;
  return Math.min(Math.max((g / positionScale.value) * 100, 0), 100);
});

const pointsLabelEndAlign = computed(
  () => !isSubpage.value && goalMarkerLeftPct.value >= 78
);

const MARKER_RADIUS_PX = 18;
const GAP_AFTER_MARKER_PX = 12;
const GOAL_ACHIEVED_GAP_PX = 10;

const goalGapHalfPx = computed(() => GOAL_ACHIEVED_GAP_PX / 2);

const goalReachedFillStyle = computed(() => {
  const pct = goalMarkerLeftPct.value;
  if (pct <= 0) return { width: "0" };
  if (showGoalAchievedUi.value) {
    return { width: `calc(${pct}% - ${goalGapHalfPx.value}px)` };
  }
  return { width: `calc(${pct}% - ${MARKER_RADIUS_PX}px)` };
});

const beyondGoalFillStyle = computed(() => {
  const start = goalMarkerLeftPct.value;
  const end = progressPct.value;
  if (end <= start) return { display: "none" };
  if (showGoalAchievedUi.value) {
    return {
      left: `calc(${start}% + ${goalGapHalfPx.value}px)`,
      width: `calc(${end - start}% - ${GOAL_ACHIEVED_GAP_PX}px)`,
    };
  }
  return {
    left: `calc(${start}% + ${MARKER_RADIUS_PX + GAP_AFTER_MARKER_PX}px)`,
    width: `calc(${end - start}% - ${MARKER_RADIUS_PX + GAP_AFTER_MARKER_PX}px)`,
  };
});

const barSegments = computed(() => {
  if (showGoalAchievedUi.value) {
    const g = goalMarkerLeftPct.value;
    return [
      { startPct: 0, endPct: g, gapLeft: false, gapRight: true },
      { startPct: g, endPct: 100, gapLeft: true, gapRight: false },
    ].filter((s) => s.endPct > s.startPct);
  }

  const DASHBOARD_TRACK_END_PCT = 95;

  if (!isSubpage.value) {
    return [
      {
        startPct: 0,
        endPct: DASHBOARD_TRACK_END_PCT,
        gapLeft: false,
        gapRight: false,
      },
    ];
  }

  const positions = uhidVisibleTicks.value
    .map((l) => getTickPosition(l.threshold))
    .filter((p) => Number.isFinite(p))
    .sort((a, b) => a - b);
  const segs = [];
  let prev = 0;
  let prevHasMarker = false;
  positions.forEach((p) => {
    segs.push({
      startPct: prev,
      endPct: p,
      gapLeft: prevHasMarker,
      gapRight: true,
    });
    prev = p;
    prevHasMarker = true;
  });
  segs.push({
    startPct: prev,
    endPct: 100,
    gapLeft: prevHasMarker,
    gapRight: false,
  });
  return segs.filter((s) => s.endPct > s.startPct);
});

const segmentStyle = (seg) => {
  if (showGoalAchievedUi.value) {
    const g = GOAL_ACHIEVED_GAP_PX / 2;
    return {
      left: seg.gapLeft ? `calc(${seg.startPct}% + ${g}px)` : `${seg.startPct}%`,
      right: seg.gapRight ? `calc(${100 - seg.endPct}% + ${g}px)` : `${100 - seg.endPct}%`,
    };
  }

  const r = MARKER_RADIUS_PX;
  const g = GAP_AFTER_MARKER_PX;
  return {
    left: seg.gapLeft
      ? `calc(${seg.startPct}% + ${r + g}px)`
      : `${seg.startPct}%`,
    right: seg.gapRight
      ? `calc(${100 - seg.endPct}% - ${r}px)`
      : `${100 - seg.endPct}%`,
  };
};

function goalPillAlignClass(pct) {
  if (pct >= 78) return "bonus-uhid__goal-pill--end";
  if (pct <= 12) return "bonus-uhid__goal-pill--start";
  return "";
}

const goalAchievedPillStyle = computed(() => ({
  left: `${goalMarkerLeftPct.value}%`,
  transform: "translateX(-50%)",
}));
</script>

<style scoped lang="scss">
@import "@/styles/portal-home/bonus-uhid-widget";
</style>
